import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, BookOpen, Handshake, Megaphone, Sparkles } from 'lucide-react';
import { MarketingShell } from './MarketingShell';

interface Post {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  readMins: number;
  body: string[];
}

export const POSTS: Post[] = [
  {
    slug: 'announcing-per-business-databases',
    title: 'Announcing per-business dedicated databases',
    excerpt: 'Every new business can now run on its own isolated database — faster, safer, and ready for scale.',
    date: '14 June 2026',
    category: 'Announcements',
    readMins: 3,
    body: [
      'Until now, businesses on Possignin shared one large database, kept apart by strict isolation. It worked — but as shops grew, we wanted more headroom and stronger guarantees.',
      'Starting today, every new business can be provisioned its own dedicated database. Your data sits alone, backups are per-business, and a busy neighbour can never slow you down. If you prefer, you can even bring your own database server.',
      'Existing businesses keep working exactly as before, and can move to a dedicated database whenever they’re ready. Nothing changes unless you want it to.',
    ],
  },
  {
    slug: 'stop-running-your-shop-on-a-notebook',
    title: 'Stop running your shop on a notebook',
    excerpt: 'The hidden cost of paper ledgers — and the five numbers you should see every evening.',
    date: '12 June 2026',
    category: 'Playbook',
    readMins: 4,
    body: [
      'A notebook never tells you that your best-selling item is about to run out, or that one customer’s dues have quietly grown for three months. It just records — it never warns.',
      'When you move to a POS, the first thing you get back is the evening close. Instead of adding up slips by hand, you see today’s sales, your cash in drawer, what sold, and what’s low — in one screen, before you lock up.',
      'The five numbers worth watching every day: total sales, cash collected, top item, low-stock count, and outstanding dues. Possignin puts all five on your dashboard, so the close takes a minute, not an hour.',
    ],
  },
  {
    slug: 'taka-first-billing',
    title: 'Why billing should be taka-first, not dollar-translated',
    excerpt: 'Rounding, SSLCommerz, and cash-to-office — the small things that make local billing actually work.',
    date: '5 June 2026',
    category: 'Product',
    readMins: 3,
    body: [
      'Most POS software was built for dollars and bolted on other currencies later. You feel it in the rounding, the receipt formats, and the payment options that don’t include the ones your customers actually use.',
      'Possignin is taka-first. Prices, invoices, and reports are in BDT from the ground up. Online payments go through SSLCommerz; for everything else, you can pay your subscription as cash to our Sylhet office.',
      'It sounds small. But when the numbers match what’s in the drawer to the poisha, you stop second-guessing the software — and start trusting it.',
    ],
  },
  {
    slug: 'one-system-instead-of-six',
    title: 'One system instead of six',
    excerpt: 'Your register, spreadsheet, payroll book, and chat group are four versions of the truth. Here’s how to get to one.',
    date: '28 May 2026',
    category: 'Playbook',
    readMins: 5,
    body: [
      'Every growing shop ends up with the same mess: a register for sales, a spreadsheet for stock, a notebook for staff, and a chat group for everything else. Each holds a slightly different version of the truth.',
      'The fix isn’t more discipline — it’s fewer places. When sales, inventory, staff, and customers live in one system, a sale updates stock, stock triggers a reorder, and the day’s total is just there.',
      'You don’t have to switch everything at once. Start with sales and inventory, get comfortable, then add HRM, CRM, or manufacturing when you’re ready. Possignin grows in the order that suits you.',
    ],
  },
  {
    slug: 'bangla-first-receipts',
    title: 'Bangla-first receipts, end to end',
    excerpt: 'Switch language per user and watch receipts, reports, and the POS follow — no half-translated screens.',
    date: '18 May 2026',
    category: 'Product',
    readMins: 2,
    body: [
      'Language shouldn’t be an afterthought. In Possignin, each user picks Bangla or English, and the choice carries everywhere — the sell screen, the reports, and the printed receipt your customer takes home.',
      'We use a proper Bangla webfont so receipts print cleanly on thermal printers, and numbers stay readable. No tofu boxes, no broken characters.',
    ],
  },
  {
    slug: 'reseller-program',
    title: 'Sell Possignin under your own brand',
    excerpt: 'How the reseller program works — your domain, your pricing, our infrastructure.',
    date: '12 May 2026',
    category: 'Partners',
    readMins: 3,
    body: [
      'If you already sell software or services to local businesses, the reseller program lets you offer a full POS under your own brand — without building one.',
      'You get a branded subdomain (or your own domain), your own packages and pricing above a fair floor, and a panel to manage your customers. We handle the platform, the payments, and the uptime.',
      'You earn commission on every business you bring in, paid on a regular cycle. Apply through “Become a reseller” and we usually review within 1–2 business days.',
    ],
  },
];

const CATEGORY_META: Record<string, { icon: typeof BookOpen; tint: string; chip: string }> = {
  Announcements: { icon: Megaphone, tint: 'from-sky-500/20 to-sky-500/5', chip: 'text-sky-700 dark:text-sky-300' },
  Playbook: { icon: BookOpen, tint: 'from-brand/20 to-brand/5', chip: 'text-brand dark:text-brand-dm' },
  Product: { icon: Sparkles, tint: 'from-emerald-500/20 to-emerald-500/5', chip: 'text-emerald-700 dark:text-emerald-300' },
  Partners: { icon: Handshake, tint: 'from-amber-500/20 to-amber-500/5', chip: 'text-amber-700 dark:text-amber-300' },
};

function Thumb({ category, className = '' }: { category: string; className?: string }) {
  const meta = CATEGORY_META[category] ?? CATEGORY_META.Product!;
  const Icon = meta.icon;
  return (
    <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${meta.tint} border border-zinc-200/70 dark:border-zinc-800/70 grid place-items-center ${className}`}>
      <Icon className="w-10 h-10 text-zinc-900/30 dark:text-white/30" />
      <span className="absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-700/80 dark:text-zinc-200/80">
        {category}
      </span>
    </div>
  );
}

export function Blog() {
  const categories = ['All', ...Array.from(new Set(POSTS.map((p) => p.category)))];
  const [active, setActive] = useState('All');
  const filtered = active === 'All' ? POSTS : POSTS.filter((p) => p.category === active);
  const [featured, ...rest] = filtered;

  return (
    <MarketingShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-20">
        <header>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">Newsroom</h1>
          <p className="mt-3 text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl leading-relaxed">
            Announcements, product notes, and practical writing on running a better shop in Bangladesh.
          </p>
        </header>

        {/* Category filter */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-zinc-200 dark:border-zinc-800 pb-4">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={
                'px-3.5 py-1.5 rounded-full text-sm font-medium transition ' +
                (active === c
                  ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                  : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800')
              }
            >
              {c}
            </button>
          ))}
        </div>

        {/* Featured */}
        {featured && (
          <Link
            to="/blog/$slug"
            params={{ slug: featured.slug }}
            className="group mt-10 grid lg:grid-cols-2 gap-6 lg:gap-10 items-center"
          >
            <Thumb category={featured.category} className="aspect-[16/10] w-full" />
            <div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                <span className={`font-semibold uppercase tracking-wider ${CATEGORY_META[featured.category]?.chip ?? ''}`}>{featured.category}</span>
                <span aria-hidden="true">·</span>
                <span>{featured.date}</span>
                <span aria-hidden="true">·</span>
                <span className="tabular-nums">{featured.readMins} min read</span>
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-tight group-hover:text-brand dark:group-hover:text-brand-dm transition">
                {featured.title}
              </h2>
              <p className="mt-3 text-zinc-600 dark:text-zinc-300 leading-relaxed">{featured.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand dark:text-brand-dm">
                Read story <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        )}

        {/* Grid */}
        {rest.length > 0 && (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {rest.map((p) => (
              <Link key={p.slug} to="/blog/$slug" params={{ slug: p.slug }} className="group flex flex-col">
                <Thumb category={p.category} className="aspect-[16/10] w-full" />
                <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <span className={`font-semibold uppercase tracking-wider ${CATEGORY_META[p.category]?.chip ?? ''}`}>{p.category}</span>
                  <span aria-hidden="true">·</span>
                  <span>{p.date}</span>
                </div>
                <h3 className="mt-2 text-lg font-semibold text-zinc-950 dark:text-zinc-50 leading-snug group-hover:text-brand dark:group-hover:text-brand-dm transition">
                  {p.title}
                </h3>
                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </MarketingShell>
  );
}

export function BlogPost() {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const post = POSTS.find((p) => p.slug === slug);

  return (
    <MarketingShell>
      <article className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition">
          <ArrowLeft className="w-4 h-4" /> Newsroom
        </Link>

        {post ? (
          <>
            <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <span className={`font-semibold uppercase tracking-wider ${CATEGORY_META[post.category]?.chip ?? ''}`}>{post.category}</span>
              <span aria-hidden="true">·</span>
              <span>{post.date}</span>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">{post.readMins} min read</span>
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-tight">
              {post.title}
            </h1>
            <Thumb category={post.category} className="mt-7 aspect-[16/8] w-full" />
            <div className="mt-7 space-y-4">
              {post.body.map((para, i) => (
                <p key={i} className="text-[15px] sm:text-base text-zinc-700 dark:text-zinc-200 leading-relaxed">{para}</p>
              ))}
            </div>
            <div className="mt-10 pt-6 border-t border-zinc-200 dark:border-zinc-800">
              <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand/90 transition">
                Try Possignin free <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        ) : (
          <div className="mt-10 text-center text-zinc-500 dark:text-zinc-400">
            <p>That story doesn’t exist.</p>
            <Link to="/blog" className="mt-3 inline-block text-brand dark:text-brand-dm hover:underline">Back to the newsroom</Link>
          </div>
        )}
      </article>
    </MarketingShell>
  );
}
